
import { Button } from "@/components/ui/button"
import { toast } from "sonner"
import { useState, useEffect } from "react"
import { useMutation } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { CertFormFields } from "./CertFormFields"
import { CertSuccess } from "./CertSuccess"

export const CertForm = () => {
  const [formData, setFormData] = useState({
    domain: '',
    name: '',
    type: 'personal',
    email: '',
  })

  const [recordNumber, setRecordNumber] = useState<string>('')
  const [submittedData, setSubmittedData] = useState<{
    certNumber: string;
    domain: string;
  } | null>(null)

  const generateRecordNumber = async () => {
    const { data: lastRecord } = await supabase
      .from('cert_applications')
      .select('record_number')
      .not('record_number', 'is', null)
      .order('created_at', { ascending: false })
      .limit(1)

    const today = new Date()
    const yearStr = today.getFullYear().toString().slice(-2)
    const monthStr = (today.getMonth() + 1).toString().padStart(2, '0')
    const dayStr = today.getDate().toString().padStart(2, '0')
    const baseNumber = `${yearStr}${monthStr}${dayStr}`
    
    let sequence = 1
    if (lastRecord && lastRecord[0]?.record_number) {
      const lastDate = lastRecord[0].record_number.slice(0, 6)
      if (lastDate === baseNumber) {
        sequence = parseInt(lastRecord[0].record_number.slice(6)) + 1
      }
    }
    
    return `${baseNumber}${sequence.toString().padStart(2, '0')}`
  }

  useEffect(() => {
    generateRecordNumber().then(setRecordNumber)
  }, [])

  const handleFieldChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const submitMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const { error } = await supabase
        .from('cert_applications')
        .insert({
          domain: data.domain,
          applicant_name: data.name,
          applicant_type: data.type,
          email: data.email,
          cert_number: recordNumber,
          verification_status: 'unverified',
          status: 'pending',
          record_number: recordNumber
        })

      if (error) throw error

      // Send email notification to user
      const { error: emailError } = await supabase.functions.invoke('send-cert-notification', {
        body: {
          certNumber: recordNumber,
          email: data.email,
          applicantName: data.name,
          domain: data.domain
        }
      })

      if (emailError) {
        console.error('Email notification failed:', emailError)
      }

      // Send admin notification
      const { error: adminNotifyError } = await supabase.functions.invoke('notify-admin', {
        body: {
          certNumber: recordNumber,
          domain: data.domain,
          applicantName: data.name,
          applicantType: data.type
        }
      })

      if (adminNotifyError) {
        console.error('Admin notification failed:', adminNotifyError)
      }

      return { certNumber: recordNumber, domain: data.domain }
    },
    onSuccess: (data) => {
      toast.success("申请已提交", {
        description: "我们已经通过邮件发送了申请详情。",
      })
      setSubmittedData(data)
      setFormData({
        domain: '',
        name: '',
        type: 'personal',
        email: ''
      })
      // Generate new record number for next submission
      generateRecordNumber().then(setRecordNumber)
    },
    onError: (error: Error) => {
      toast.error("提交失败", {
        description: error.message,
      })
    }
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    submitMutation.mutate(formData)
  }

  return (
    <div className="space-y-8">
      <form onSubmit={handleSubmit} className="space-y-6 max-w-md mx-auto">
        <CertFormFields 
          formData={formData}
          recordNumber={recordNumber}
          onChange={handleFieldChange}
        />
        
        <Button 
          type="submit" 
          className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:opacity-90 transition-opacity"
          disabled={submitMutation.isPending}
        >
          {submitMutation.isPending ? "提交中..." : "提交申请"}
        </Button>
      </form>

      {submittedData && (
        <CertSuccess 
          certNumber={submittedData.certNumber}
          domain={submittedData.domain}
        />
      )}
    </div>
  )
}
